import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { computeContainRect } from '../../domain/liveView/mediaContainGeometry';

export interface ContainedMediaOverlayProps {
  src: string;
  alt: string;
  overlay?: React.ReactNode;
  backgroundColor?: string;
  imageStyle?: React.CSSProperties;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
}

type Size = {
  width: number;
  height: number;
};

/** H.264 macroblock padding. 1080 lines code as 1088; the extra 8 lines sit at the bottom. */
const PADDED_CODED_WIDTH = 1920;
const PADDED_CODED_HEIGHT = 1088;
const VISIBLE_FRAME_HEIGHT = 1080;

export function visibleCodedFrame(width: number, height: number): {
  width: number;
  height: number;
  cropBottomPx: number;
} {
  if (width === PADDED_CODED_WIDTH && height === PADDED_CODED_HEIGHT) {
    return { width: PADDED_CODED_WIDTH, height: VISIBLE_FRAME_HEIGHT, cropBottomPx: 8 };
  }
  return { width, height, cropBottomPx: 0 };
}

function readElementSize(node: HTMLDivElement | null): Size {
  if (!node) return { width: 0, height: 0 };
  const rect = node.getBoundingClientRect();
  return {
    width: rect.width || node.clientWidth || 0,
    height: rect.height || node.clientHeight || 0,
  };
}

export function ContainedMediaOverlay({
  src,
  alt,
  overlay,
  backgroundColor = 'transparent',
  imageStyle,
  onLoad,
  onError,
}: ContainedMediaOverlayProps) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [frameSize, setFrameSize] = useState<Size>({ width: 0, height: 0 });
  const [mediaSize, setMediaSize] = useState<Size>({ width: 0, height: 0 });

  useEffect(() => {
    const node = frameRef.current;
    if (!node) return undefined;

    const sync = () => setFrameSize(readElementSize(node));
    sync();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', sync);
      return () => window.removeEventListener('resize', sync);
    }

    const observer = new ResizeObserver(() => sync());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visible = visibleCodedFrame(mediaSize.width, mediaSize.height);
  useEffect(() => {
    const node = imageRef.current;
    if (!node) return undefined;
    if (visible.cropBottomPx > 0) {
      node.style.setProperty('object-view-box', `inset(0px 0px ${visible.cropBottomPx}px 0px)`);
    } else {
      node.style.removeProperty('object-view-box');
    }
    return undefined;
  }, [src, visible.cropBottomPx]);
  const overlayRect = useMemo(
    () => computeContainRect(frameSize.width, frameSize.height, visible.width, visible.height),
    [frameSize.height, frameSize.width, visible.height, visible.width],
  );

  return (
    <Box
      ref={frameRef}
      sx={{
        position: 'absolute',
        inset: 0,
      }}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        data-intrinsic-width={mediaSize.width || undefined}
        data-intrinsic-height={mediaSize.height || undefined}
        data-display-width={visible.width || undefined}
        data-display-height={visible.height || undefined}
        data-crop-bottom-px={String(visible.cropBottomPx)}
        data-object-fit="contain"
        data-object-position="center center"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          objectPosition: 'center center',
          backgroundColor,
          zIndex: 0,
          ...imageStyle,
        }}
        onLoad={(event) => {
          const nextWidth = event.currentTarget.naturalWidth || event.currentTarget.width || 0;
          const nextHeight = event.currentTarget.naturalHeight || event.currentTarget.height || 0;
          setMediaSize({ width: nextWidth, height: nextHeight });
          onLoad?.(event);
        }}
        onError={onError}
      />
      {overlay && overlayRect.width > 0 && overlayRect.height > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            left: `${overlayRect.left}px`,
            top: `${overlayRect.top}px`,
            width: `${overlayRect.width}px`,
            height: `${overlayRect.height}px`,
            pointerEvents: 'none',
            zIndex: 2,
          }}
          data-testid="contained-media-overlay-frame"
        >
          {overlay}
        </Box>
      ) : null}
    </Box>
  );
}

export default ContainedMediaOverlay;
